import React from "react";
import {
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaXTwitter,
} from "react-icons/fa6";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { IoCallOutline, IoMailOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();

  const quickLinks = [
    { name: "Home", hash: "/", isHome: true },
    { name: "About", hash: "#about" },
    { name: "Services", hash: "#Services" },
    { name: "For Providers", hash: "#for-providers" },
    { name: "Contact", hash: "#Contact" },
  ];

  const socialLinks = [
    { icon: <FaInstagram />, link: "https://www.instagram.com/livmohealth/" },
    { icon: <FaLinkedinIn />, link: "https://www.linkedin.com/company/livmo-health/" },
    { icon: <FaYoutube />, link: "https://www.youtube.com/@LivmoHealth" },
    { icon: <FaXTwitter />, link: "https://x.com/HealthLivm28519" },
  ];

  const handleLinkClick = (item) => {
    if (item.isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      navigate("/");
    } else if (item.isContact) {
      navigate("/contact");
    } else {
      const element = document.querySelector(item.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(`/${item.hash}`);
      }
    }
  };

  return (
    <footer className="relative mt-0 bg-gradient-to-br from-white via-secondary/10 to-white border-t border-primary/10 overflow-hidden">
      {/* Smaller background glows */}
      <div className="absolute top-[-40px] left-[-40px] w-[120px] h-[120px] bg-primary/5 blur-2xl rounded-full" />
      <div className="absolute bottom-[-40px] right-[-40px] w-[120px] h-[120px] bg-primary/5 blur-2xl rounded-full" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 py-4 md:px-6 md:py-6">
        
        {/* TOP SECTION – single column on mobile, then 2 cols on md, 4 on xl */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          
          {/* BRAND – stays full width */}
          <div>
            <img
              src={require("../../assets/logo.png")}
              alt="Livmo"
              className="h-8 md:h-10 object-contain"
            />
            <p className="mt-2 text-xs leading-relaxed text-text1/75 font-poppins max-w-[260px]">
              Livmo is India's first healthcare coordination platform connecting subscribers with verified providers.
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-2 mt-3">
              {socialLinks.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  className="w-7 h-7 rounded-lg bg-white border border-primary/15 shadow-sm flex items-center justify-center text-primary text-xs transition-all duration-300 hover:bg-primary hover:text-white"
                >
                  {item.icon}
                </a>
              ))}
            </div>
          </div>

          {/* QUICK LINKS – 2 columns on mobile, 1 column on larger screens */}
          <div>
            <h3 className="text-sm font-bold text-primary font-poppins mb-2">
              Quick Links
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-1.5">
              {quickLinks.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleLinkClick(item)}
                  className="text-xs text-text1/75 font-medium font-poppins transition-all duration-300 hover:text-primary text-left"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* OFFICES */}
          <div>
            <h3 className="text-sm font-bold text-primary font-poppins mb-2">
              Offices
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="min-w-[28px] h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs">
                  <HiOutlineLocationMarker className="text-sm" />
                </div>
                <div>
                  <h4 className="font-semibold text-xs text-darkblue">Registered Office</h4>
                  <p className="text-[11px] leading-relaxed text-text1/70">
                    No. 36/3 Otheri Road, Swamy Nagar, Otheri, Vellore, Tamil Nadu - 632002
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CONTACT INFO + CTA */}
          <div>
            <h3 className="text-sm font-bold text-primary font-poppins mb-2">
              Contact Info
            </h3>
            <div className="space-y-2.5">
              <a href="tel:+917200995976" className="flex items-center gap-2 group">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs group-hover:bg-primary group-hover:text-white transition-all">
                  <IoCallOutline />
                </div>
                <div>
                  <p className="text-[10px] text-text1/60">Phone</p>
                  <p className="text-xs text-text1 font-semibold">+91 72009 95976</p>
                </div>
              </a>
              <a href="mailto:info@livmohealth.com" className="flex items-center gap-2 group">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs group-hover:bg-primary group-hover:text-white transition-all">
                  <IoMailOutline />
                </div>
                <div>
                  <p className="text-[10px] text-text1/60">Email</p>
                  <p className="text-xs text-text1 font-semibold">info@livmohealth.com</p>
                </div>
              </a>
            </div>
            
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="border-t border-primary/10 mt-5 pt-3 flex flex-col lg:flex-row items-center justify-between gap-1.5">
          <p className="text-[10px] text-text1/60 text-center lg:text-left">
            © 2026 Livmo Private Limited. All rights reserved.
          </p>
          <p className="text-[10px] text-text1/60 text-center">
            India's Healthcare Coordination Platform
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;